import { useEffect, useState, type CSSProperties } from 'react';
import { useDispatch } from 'react-redux';
import { Text } from '@belong/ui';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { showToast } from 'store/redux/toast';
import { getString } from 'strings';
import { POST_INSPECTION_FLOW_STRINGS } from 'strings/post-inspection-flow';
import PostInspectionContentLayout from '../../../PostInspectionContentLayout/PostInspectionContentLayout';
import { getPricingAssignedEmployee } from '../utils';

const S = POST_INSPECTION_FLOW_STRINGS;
const ICONS = '/post_inspection/tour_strategy';
const RECOMMENDED_BADGE_GRADIENT =
  'linear-gradient(77deg, #3EE4A9 30%, #119EE4 92%)';

const formatAmount = (amount: number) =>
  `$${Math.round(amount).toLocaleString('en-US')}`;

type Benefit = { icon: string; title: string; description: string };

const selfTourBenefits = (holdAmount: number | null): Benefit[] => [
  {
    icon: `${ICONS}/verified-id.png`,
    title: S['pricing.tour-strategy.self-tours.verified-id.title'],
    description: S['pricing.tour-strategy.self-tours.verified-id.description'],
  },
  {
    icon: `${ICONS}/one-time-access.png`,
    title: S['pricing.tour-strategy.self-tours.one-time-access.title'],
    description:
      S['pricing.tour-strategy.self-tours.one-time-access.description'],
  },
  ...(holdAmount == null
    ? []
    : [
        {
          icon: `${ICONS}/refundable-hold.png`,
          title: getString(
            S['pricing.tour-strategy.self-tours.refundable-hold.title'],
            { holdAmount: formatAmount(holdAmount) },
          ),
          description:
            S['pricing.tour-strategy.self-tours.refundable-hold.description'],
        },
      ]),
  {
    icon: `${ICONS}/photos-review.png`,
    title: S['pricing.tour-strategy.self-tours.photos-review.title'],
    description:
      S['pricing.tour-strategy.self-tours.photos-review.description'],
  },
];

const whiteGloveBenefits: Benefit[] = [
  {
    icon: `${ICONS}/same-advisor.png`,
    title: S['pricing.tour-strategy.white-glove-details.same-advisor.title'],
    description:
      S['pricing.tour-strategy.white-glove-details.same-advisor.description'],
  },
  {
    icon: `${ICONS}/private-tour.png`,
    title: S['pricing.tour-strategy.white-glove-details.home-readied.title'],
    description:
      S['pricing.tour-strategy.white-glove-details.home-readied.description'],
  },
  {
    icon: `${ICONS}/feedback.png`,
    title: S['pricing.tour-strategy.white-glove-details.feedback.title'],
    description:
      S['pricing.tour-strategy.white-glove-details.feedback.description'],
  },
  {
    icon: `${ICONS}/secured-by-hand.png`,
    title: S['pricing.tour-strategy.white-glove-details.secured.title'],
    description:
      S['pricing.tour-strategy.white-glove-details.secured.description'],
  },
];

const BenefitGrid = ({
  title,
  subtitle,
  benefits,
}: {
  title: string;
  subtitle: string;
  benefits: Benefit[];
}) => (
  <div className="mt-2xl">
    <Text variant="h3" fontWeight="semibold">
      {title}
    </Text>
    <Text variant="p1" className="mt-2sm">
      {subtitle}
    </Text>
    <div className="mt-lg grid grid-cols-1 gap-x-xl gap-y-md md:grid-cols-2">
      {benefits.map((benefit) => (
        <div key={benefit.title}>
          <img className="h-[48px] w-[48px]" src={benefit.icon} alt="" />
          <Text className="mt-xs" fontWeight="semibold">
            {benefit.title}
          </Text>
          <Text variant="p1" className="mt-xs">
            {benefit.description}
          </Text>
        </div>
      ))}
    </div>
  </div>
);

const OptionCard = ({
  name,
  badge,
  description,
  price,
  priceCaption,
  badgeClassName,
  badgeStyle,
  isSelected,
  onSelect,
}: {
  name: string;
  badge: string;
  description: string;
  price: string;
  priceCaption: string;
  badgeClassName?: string;
  badgeStyle?: CSSProperties;
  isSelected: boolean;
  onSelect: () => void;
}) => (
  <label
    className={clsx(
      'flex h-full cursor-pointer flex-col rounded p-sm transition-colors',
      isSelected ? 'bg-navy' : 'border border-solid border-gray bg-transparent',
    )}
  >
    <input
      type="radio"
      name="tour-strategy"
      className="sr-only"
      checked={isSelected}
      onChange={onSelect}
    />
    <div className="flex items-center justify-between gap-sm">
      <Text
        fontWeight="semibold"
        className={isSelected ? 'text-white' : undefined}
      >
        {name}
      </Text>
      <div
        className={clsx('rounded px-xs py-2xs', badgeClassName)}
        style={badgeStyle}
      >
        <Text variant="p1" fontWeight="semibold" className="text-white">
          {badge}
        </Text>
      </div>
    </div>
    <Text variant="p1" className="mt-xs text-dark-gray">
      {description}
    </Text>
    <div className="mt-auto flex items-baseline gap-xs pt-xs md:pt-md">
      <Text
        variant="h3"
        fontWeight="semibold"
        className={isSelected ? 'text-white' : undefined}
      >
        {price}
      </Text>
      <Text variant="p1" className="text-dark-gray">
        {priceCaption}
      </Text>
    </div>
  </label>
);

type Props = {
  onSaveAndNext: (values: {
    tourStrategy: { homeownerAgreedToSelfTours: boolean };
  }) => Promise<void>;
  stepConfig: any;
  stepConfigs: any;
  stepData: any;
  stepResource: any;
};

const TourStrategy = ({
  stepResource,
  stepData,
  stepConfig,
  stepConfigs,
  onSaveAndNext,
}: Props) => {
  const dispatch = useDispatch();
  const [agreedToSelfTours, setAgreedToSelfTours] = useState<boolean | null>(
    null,
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setAgreedToSelfTours(
      stepData?.tourStrategy?.homeownerAgreedToSelfTours ?? null,
    );
  }, [stepData]);

  const tourStrategy = stepData?.tourStrategy;
  if (
    !stepResource ||
    !tourStrategy ||
    !Number.isFinite(tourStrategy.hostedToursOnlyFeeAmount)
  ) {
    return null;
  }

  const assignedEmployee = getPricingAssignedEmployee(stepResource);
  const { hostedToursOnlyFeeAmount, selfTourHoldAmount } = tourStrategy;
  const feeAmount = formatAmount(hostedToursOnlyFeeAmount);
  const hasSelection = agreedToSelfTours !== null;

  const handleDone = async () => {
    setIsSaving(true);

    try {
      await onSaveAndNext({
        tourStrategy: { homeownerAgreedToSelfTours: agreedToSelfTours! },
      });
    } catch {
      dispatch(
        showToast({
          component: (
            <Text fontWeight="semibold">
              {S['pricing.tour-strategy.save-error']}
            </Text>
          ),
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PostInspectionContentLayout
      showCityBackground={false}
      noOverlay
      stepConfigs={stepConfigs}
      stepConfig={stepConfig}
      title={getString(S['pricing.tour-strategy.title'], {
        firstName: stepResource.homeListingDetailsModel?.owner?.firstName,
      })}
      employee={assignedEmployee}
    >
      <Text
        variant="h3"
        fontWeight="semibold"
        className="mt-xl"
        id="tour-strategy-heading"
      >
        <span className="hidden md:inline">
          {S['pricing.tour-strategy.heading']}
        </span>
        <span className="md:hidden">
          {S['pricing.tour-strategy.heading-mobile']}
        </span>
      </Text>

      <div
        className="mt-md grid grid-cols-1 gap-md md:grid-cols-2"
        role="radiogroup"
        aria-labelledby="tour-strategy-heading"
      >
        <OptionCard
          name={S['pricing.tour-strategy.full-access.name']}
          badge={S['pricing.tour-strategy.full-access.badge']}
          description={S['pricing.tour-strategy.full-access.description']}
          price={S['pricing.tour-strategy.full-access.price']}
          priceCaption={S['pricing.tour-strategy.full-access.price-caption']}
          badgeStyle={{ background: RECOMMENDED_BADGE_GRADIENT }}
          isSelected={agreedToSelfTours === true}
          onSelect={() => setAgreedToSelfTours(true)}
        />
        <OptionCard
          name={S['pricing.tour-strategy.white-glove.name']}
          badge={S['pricing.tour-strategy.white-glove.badge']}
          description={S['pricing.tour-strategy.white-glove.description']}
          price={feeAmount}
          priceCaption={S['pricing.tour-strategy.white-glove.price-caption']}
          badgeClassName={agreedToSelfTours === false ? 'bg-green' : 'bg-navy'}
          isSelected={agreedToSelfTours === false}
          onSelect={() => setAgreedToSelfTours(false)}
        />
      </div>

      {agreedToSelfTours === true && (
        <BenefitGrid
          title={S['pricing.tour-strategy.self-tours.title']}
          subtitle={S['pricing.tour-strategy.self-tours.subtitle']}
          benefits={selfTourBenefits(selfTourHoldAmount)}
        />
      )}

      {agreedToSelfTours === false && (
        <BenefitGrid
          title={S['pricing.tour-strategy.white-glove-details.title']}
          subtitle={S['pricing.tour-strategy.white-glove-details.subtitle']}
          benefits={whiteGloveBenefits}
        />
      )}

      <div className="mt-2xl flex justify-center">
        <PostInspectionContentLayout.CtaButton
          disabled={!hasSelection || isSaving}
          onClick={handleDone}
        >
          {S['pricing.tour-strategy.cta']}
        </PostInspectionContentLayout.CtaButton>
      </div>

      {hasSelection && (
        <Text variant="p1" className="mb-3xl mt-md text-center text-dark-gray">
          {agreedToSelfTours
            ? S['pricing.tour-strategy.full-access.footnote']
            : getString(S['pricing.tour-strategy.white-glove.footnote'], {
                feeAmount,
              })}
        </Text>
      )}
    </PostInspectionContentLayout>
  );
};

TourStrategy.propTypes = {
  stepResource: PropTypes.object.isRequired,
  onSaveAndNext: PropTypes.func.isRequired,
  stepConfigs: PropTypes.arrayOf(PropTypes.object).isRequired,
  stepConfig: PropTypes.object.isRequired,
  stepData: PropTypes.object,
};

export default TourStrategy;
